import api, { ListResponse } from "../api";
import { OrionTransaction } from "helpers/types";
import { HeldPeriod, serializeHeldPeriods } from "./heldPeriods";

export enum BucketNumberEnum {
  BUCKET_1 = 1,
  BUCKET_2 = 2,
  BUCKET_3 = 3,
}

export interface BucketAsset {
  id: number;
  bucket: number;
  asset: number;
  symbol: string;
  asset__name: string;
  is_main: boolean;
  included: boolean;
  // Live backend check: does the pair's AccountHistory (PORTFOLIO chain)
  // exist? Reports need it.
  has_history: boolean;
  // Live backend check: is the security in the account's holdings NOW?
  // Never relative to the report's periods: with periods "add all
  // holdings" adds what was held ACROSS them, so a member it added can
  // legitimately show as not held — the position has left the account
  // since, which is the one thing the period set cannot tell you.
  is_held: boolean;
  // Live backend check, answered only when the bucket list was asked over
  // the report's periods (`held_periods`): was the security held by the
  // account at any point inside them? False without periods — "held during
  // no period at all" is nothing, never everything — so a caller must have
  // sent periods before reading it as a fact about a member.
  //
  // Distinct from `is_held` above, and the pair is not redundant: this one
  // decides whether the report counts the member at all (one whose holdings
  // never overlap the period is left out of the bucket's totals), while
  // `is_held` says whether the position has left the account since.
  held_in_period: boolean;
  // Why the last history auto-generation attempt failed; null after success.
  history_error: string | null;
}

export interface BucketHistoryFailure {
  symbol: string;
  error: string;
}

// Bucket 2 "add all holdings" also auto-generates account histories and
// reports which pairs failed; adds bigger than the backend's inline limit
// defer generation instead (history_pending) for the batched Generate
// histories flow — a period-wide add reaches that limit far more often
// than a current-holdings one, since it covers everything the account held
// across the report rather than only what it still owns. Bucket 3 sync
// never generates histories (its report values are calculated
// dynamically) — both fields stay empty there.
export interface AddedAssetsResponse {
  added: number;
  history_failed: BucketHistoryFailure[];
  history_pending: number;
  // Symbols left out because the account's other exclusive bucket already
  // holds them — Bucket 1 and Bucket 2 never share a security. Always empty
  // for Bucket 3.
  skipped_other_bucket: string[];
}

// One batch of history generation. The caller loops, passing
// next_after_id back as afterId, until remaining hits 0 — failed members
// keep their stored error but the cursor moves past them.
export interface EnsureHistoryResponse {
  processed: number;
  failed: BucketHistoryFailure[];
  remaining: number;
  next_after_id: number | null;
}

export interface Bucket {
  id: number;
  account: number;
  account__number: string;
  number: BucketNumberEnum;
  // The date the bucket came into existence, as `YYYY-MM-DD`. `null` is a
  // real state the backend serves on purpose — nobody has set one — and is
  // what the modal's Clear action writes. It is never a stand-in for when
  // the row was created, and nothing calculates from it yet.
  inception_date: string | null;
  assets: BucketAsset[];
}

export interface BucketListParams {
  account?: number;
  account__client?: number;
  // The report's periods, serialized by `serializeHeldPeriods`. Passing
  // them is what fills each member's `held_in_period`; a screen with no
  // reporting period omits the parameter.
  held_periods?: string;
}

// Create returns a light payload (no nested members — a populated Bucket 3
// can hold ~1000 of them); `added` describes the Bucket 3 auto-population.
export interface CreateBucketResponse {
  id: number;
  account: number;
  number: BucketNumberEnum;
  added: number;
}

// The Strategy Report's "Bucket Inception" period over the report's buckets
// (the account's Bucket 1 and 2 plus the chosen Bucket 3), from the earliest
// `inception_date` through the previous business day. Both dates are null
// when no range exists, and `reason` then says why.
export type BucketInceptionRangeReason = "no_buckets" | "no_inception_date";

export interface BucketInceptionRange {
  start_date: string | null;
  end_date: string | null;
  reason: BucketInceptionRangeReason | null;
  bucket_ids: number[];
}

const LIST_TAG = { type: "Buckets", id: "LIST" } as const;
// Member mutations touch a single bucket — invalidate just it, so only the
// cached lists that actually contain that bucket refetch.
const bucketTag = (_result: unknown, _error: unknown, arg: { bucketId: number }) => [
  { type: "Buckets", id: arg.bucketId } as const,
];

const bucketAndHistoryTags = (
  _result: unknown,
  _error: unknown,
  arg: { bucketId: number },
) => [
  { type: "Buckets", id: arg.bucketId } as const,
  "AccountHistory" as const,
];

export const apiWithBuckets = api
  .enhanceEndpoints({
    addTagTypes: ["Buckets", "AccountHistory"],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      listBuckets: builder.query<ListResponse<Bucket>, BucketListParams>({
        query: (params) => ({
          url: `/trading/buckets/`,
          params,
          method: "GET",
        }),
        providesTags: (result) => [
          LIST_TAG,
          ...(result?.results ?? []).map(
            ({ id }) => ({ type: "Buckets", id }) as const,
          ),
        ],
      }),
      getBucketInceptionRange: builder.query<
        BucketInceptionRange,
        { account: number; bucket_3?: number; today: string }
      >({
        query: (params) => ({
          url: `/trading/buckets/inception_range/`,
          params,
          method: "GET",
        }),
        // LIST catches a bucket created or deleted; the per-bucket tags
        // catch an inception-date edit on one the range was built from.
        providesTags: (result) => [
          LIST_TAG,
          ...(result?.bucket_ids ?? []).map(
            (id) => ({ type: "Buckets", id }) as const,
          ),
        ],
      }),
      // Light response on purpose — no nested members. A freshly populated
      // Bucket 3 can hold ~900 assets; the full bucket arrives via the
      // LIST refetch this mutation triggers.
      // `inception_date` is required, not optional: every UI create path
      // prompts for it first (defaulting to today) and aborts creation if
      // the prompt is cancelled, so a bucket created from the app always
      // arrives with one. The field itself stays nullable on the model —
      // buckets created before this, and by anything but the UI, still
      // carry null, and the header's Clear action writes it back.
      createBucket: builder.mutation<
        CreateBucketResponse,
        { account: number; number: BucketNumberEnum; inception_date: string }
      >({
        query: (body) => ({
          url: `/trading/buckets/`,
          method: "POST",
          body,
        }),
        invalidatesTags: [LIST_TAG],
      }),
      // The only bucket-level PATCH there is: inception_date is the one
      // mutable bucket field (the backend's BucketUpdateSerializer declares
      // nothing else, so account/number stay immutable). `null` clears it
      // back to "not set". Invalidates just this bucket, so the header
      // refetches its new value.
      updateBucketInceptionDate: builder.mutation<
        { id: number; inception_date: string | null },
        { bucketId: number; inception_date: string | null }
      >({
        query: ({ bucketId, inception_date }) => ({
          url: `/trading/buckets/${bucketId}/`,
          method: "PATCH",
          body: { inception_date },
        }),
        invalidatesTags: bucketTag,
      }),
      deleteBucket: builder.mutation<void, { id: number }>({
        query: ({ id }) => ({
          url: `/trading/buckets/${id}/`,
          method: "DELETE",
        }),
        invalidatesTags: [LIST_TAG],
      }),
      addBucketAsset: builder.mutation<
        // warning: set when the member was added but its AccountHistory
        // could not be generated.
        BucketAsset & { warning?: string },
        { bucketId: number; assetId: number }
      >({
        query: ({ bucketId, assetId }) => ({
          url: `/trading/buckets/${bucketId}/assets/`,
          method: "POST",
          body: { asset_id: assetId },
        }),
        invalidatesTags: bucketTag,
      }),
      // Buckets 1/2: deletes the member row. Bucket 3: flips it to excluded.
      removeBucketAsset: builder.mutation<
        BucketAsset | void,
        { bucketId: number; assetId: number }
      >({
        query: ({ bucketId, assetId }) => ({
          url: `/trading/buckets/${bucketId}/assets/${assetId}/`,
          method: "DELETE",
        }),
        invalidatesTags: bucketTag,
      }),
      updateBucketAsset: builder.mutation<
        BucketAsset,
        {
          bucketId: number;
          assetId: number;
          is_main?: boolean;
          included?: boolean;
        }
      >({
        query: ({ bucketId, assetId, ...patch }) => ({
          url: `/trading/buckets/${bucketId}/assets/${assetId}/`,
          method: "PATCH",
          body: patch,
        }),
        // Optimistic: flip the flag in every cached bucket list immediately,
        // roll back on failure. No tag invalidation — the server returns the
        // same state.
        async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
          const patches = apiWithBuckets.util
            .selectInvalidatedBy(getState(), [
              { type: "Buckets", id: arg.bucketId },
            ])
            .filter((entry) => entry.endpointName === "listBuckets")
            .map(({ originalArgs }) =>
              dispatch(
                apiWithBuckets.util.updateQueryData(
                  "listBuckets",
                  originalArgs,
                  (draft) => {
                    const bucket = draft.results.find(
                      (b) => b.id === arg.bucketId,
                    );
                    const member = bucket?.assets.find(
                      (a) => a.asset === arg.assetId,
                    );
                    if (!bucket || !member) return;
                    if (arg.is_main !== undefined) {
                      member.is_main = arg.is_main;
                    }
                    if (arg.included !== undefined) {
                      member.included = arg.included;
                    }
                  },
                ),
              ),
            );
          try {
            await queryFulfilled;
          } catch {
            patches.forEach((patch) => patch.undo());
          }
        },
      }),
      syncBucket: builder.mutation<AddedAssetsResponse, { bucketId: number }>({
        query: ({ bucketId }) => ({
          url: `/trading/buckets/${bucketId}/sync/`,
          method: "POST",
        }),
        invalidatesTags: bucketTag,
      }),
      // Bucket 2: add every stock the account held and isn't a member of
      // yet. With `heldPeriods` that means held ACROSS the report's
      // periods — the same set, by the same lookup, that the asset picker
      // beside the button offers; without them, what the account holds now
      // (the client page's Buckets tab, which has no reporting period, and
      // the same discovery as Bucket 3's auto-population).
      addHeldAssets: builder.mutation<
        AddedAssetsResponse,
        { bucketId: number; heldPeriods?: HeldPeriod[] }
      >({
        query: ({ bucketId, heldPeriods }) => {
          const held_periods = serializeHeldPeriods(heldPeriods);
          return {
            url: `/trading/buckets/${bucketId}/add_held_assets/`,
            method: "POST",
            // Omitted rather than sent empty: the backend reads an absent
            // parameter as "no period was chosen", which is what selects
            // the current-holdings fallback.
            body: held_periods ? { held_periods } : {},
          };
        },
        invalidatesTags: bucketTag,
      }),
      // Bucket 3 only: the account's own rows over the report period —
      // cash in and out, margin interest, dividends.
      getBucketTransactions: builder.query<
        ListResponse<OrionTransaction>,
        { bucketId: number; start_date: string; end_date: string }
      >({
        query: ({ bucketId, ...params }) => ({
          url: `/trading/buckets/${bucketId}/transactions/`,
          params,
          method: "GET",
        }),
      }),
      // One BATCH of AccountHistory generation for members that still lack
      // it (or whose last attempt failed) — loop with afterId to cover a
      // large bucket. Buckets 1/2 only: the backend rejects Bucket 3
      // (it doesn't use AccountHistory).
      ensureBucketHistory: builder.mutation<
        EnsureHistoryResponse,
        { bucketId: number; afterId?: number }
      >({
        query: ({ bucketId, afterId }) => ({
          url: `/trading/buckets/${bucketId}/ensure_history/`,
          method: "POST",
          body: afterId ? { after_id: afterId } : {},
        }),
        invalidatesTags: bucketAndHistoryTags,
      }),
    }),
  });

export const {
  useListBucketsQuery,
  useGetBucketInceptionRangeQuery,
  useCreateBucketMutation,
  useDeleteBucketMutation,
  useUpdateBucketInceptionDateMutation,
  useAddBucketAssetMutation,
  useRemoveBucketAssetMutation,
  useUpdateBucketAssetMutation,
  useSyncBucketMutation,
  useAddHeldAssetsMutation,
  useGetBucketTransactionsQuery,
  useEnsureBucketHistoryMutation,
} = apiWithBuckets;
